import * as React from 'react';
import { Button, CssBaseline, TextField, FormControlLabel, Link, Grid, Box, Typography, Radio, RadioGroup, FormControl, Paper, InputAdornment, IconButton } from '@mui/material'; // Added Paper, InputAdornment, IconButton
import Container from '@mui/material/Container';
import { toast } from "react-toastify";
import { Formik, Form, ErrorMessage } from 'formik'; // Removed validateYupSchema (not used directly)
import * as Yup from 'yup';
import { useHistory } from "react-router-dom"
import { setToken } from 'app/services/UtilServices';
import { userRegister } from 'app/services/customer/customerAPI';
import Spinner from 'components/Miscellaneous/Spinner';
// Import Icons
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const RegisterPage = () => {
    const history = useHistory(); // Use const
    const [loading, setLoading] = React.useState(false);
    const [genderValue, setGenderValue] = React.useState('male'); // Rename state for clarity
    const [showPassword, setShowPassword] = React.useState(false); // State for password visibility

    const handleChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGenderValue((event.target as HTMLInputElement).value);
    };

    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };



    const validationSchema = Yup.object().shape({
        firstName: Yup.string().max(25).required('First name is required'),
        lastName: Yup.string().max(25).required('Last name is required'),
        gender: Yup.string().max(25).required('Gender is required'),
        mobile: Yup.string()
            .required('Mobile is required')
            .matches(/^[0-9]+$/, 'Must be only digits')
            .min(10, 'Must be exactly 10 digits')
            .max(10, 'Must be exactly 10 digits'),
        email: Yup.string().email('Must be a valid email').max(50).required('Email is required'),
        password: Yup.string().min(6, 'Password must be at least 6 characters').max(50).required('Password is required'),
    });

    return (
        // Adjust container padding/margin for better spacing, especially on mobile
        <Container component="main" maxWidth="sm" sx={{ mt: { xs: 8, sm: 12 }, mb: 4 }}>
            <CssBaseline />
            {/* Wrap form in Paper for elevation and structure */}
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    {/* Adjust heading size for mobile */}
                    <Typography component="h1" variant={"h4"} sx={{ mb: 3, fontSize: { xs: '1.8rem', sm: '2.125rem' } }}>
                        Create Account
                    </Typography>
                    {/* Removed redundant Box wrapper */}
                    {loading ? <Spinner /> :

                        <Formik
                            initialValues={{
                                firstName: '',
                                lastName: '',
                                email: '',
                                mobile: '',
                                password: '',
                                gender: 'male'
                            }}
                            validationSchema={validationSchema}
                            onSubmit={async (values: any) => {
                                setLoading(true)

                                try {
                                    const data = {
                                        "firstName": values.firstName,
                                        "lastName": values.lastName,
                                        "login_type": "email",
                                        "email": values.email,
                                        "phone": values.mobile.toString(),
                                        "fullName": `${values.firstName} ${values.lastName}`, // Construct fullName
                                        "password": values.password,
                                        "gender": genderValue // Use renamed state
                                    }
                                    const result = await userRegister(data);
                                    if (result.data && result.data.token != null) {
                                        toast.success("Account created successfully");
                                        setLoading(false)
                                        setToken(result.data.token)
                                        history.push('/')
                                        window.location.reload()
                                    }

                                } catch (error: any) { // Add type to error
                                    toast.error(error?.message || "Registration failed");
                                    console.error(error);
                                } finally {
                                    setLoading(false); // Ensure loading is set to false
                                }
                            }}
                        >
                            {({
                                errors,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                isSubmitting,
                                touched,
                                values,
                                isValid,
                                dirty
                            }) => (
                                // Removed extra div wrapper
                                <Form onSubmit={handleSubmit}>
                                    <Grid container spacing={2}> {/* Increased spacing */}
                                        <Grid item xs={12} sm={6} > {/* Adjusted grid for responsiveness */}
                                            <TextField
                                                variant="outlined" // Modern variant
                                                error={Boolean(touched.firstName && errors.firstName)}
                                                autoComplete="given-name" // More specific autocomplete
                                                name="firstName"
                                                required
                                                fullWidth
                                                id="firstName"
                                                label="First Name"
                                                autoFocus
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.firstName} // Ensure value is bound
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PersonOutlineIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {/* Use Typography for error message */}
                                            <ErrorMessage name="firstName">
                                                {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                                            </ErrorMessage>

                                        </Grid>
                                        <Grid item xs={12} sm={6} > {/* Adjusted grid */}
                                            <TextField
                                                variant="outlined" // Modern variant
                                                error={Boolean(touched.lastName && errors.lastName)}
                                                autoComplete="family-name" // More specific autocomplete
                                                name="lastName"
                                                required
                                                fullWidth
                                                id="lastName"
                                                label="Last Name"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.lastName} // Ensure value is bound
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PersonOutlineIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <ErrorMessage name="lastName">
                                                {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                                            </ErrorMessage>

                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                variant="outlined" // Modern variant
                                                error={Boolean(touched.email && errors.email)}
                                                required
                                                fullWidth
                                                id="email"
                                                label="Email Address"
                                                name="email"
                                                autoComplete="email"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                type="email"
                                                value={values.email}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <EmailOutlinedIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <ErrorMessage name="email">
                                                {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                                            </ErrorMessage>

                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                variant="outlined" // Modern variant
                                                error={Boolean(touched.mobile && errors.mobile)}
                                                required
                                                fullWidth
                                                id="mobile"
                                                label="Mobile Number"
                                                name="mobile"
                                                autoComplete="mobile"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                type="number"
                                                value={values.mobile}
                                                inputProps={{ maxLength: 10, inputMode: 'numeric' }} // Input properties
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PhoneOutlinedIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <ErrorMessage name="mobile">
                                                {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                                            </ErrorMessage>

                                        </Grid>
                                        <Grid item xs={12}>
                                            <FormControl component="fieldset" sx={{ mt: 1 }}> {/* Added margin top */}
                                                <Typography variant="body2" color="text.secondary" component="legend" sx={{ mb: 1 }}>Gender</Typography> {/* Added legend */}
                                                <RadioGroup
                                                    aria-label="gender"
                                                    name="gender" // Keep name for Formik state if needed, but control via genderValue state
                                                    value={genderValue} // Control with state
                                                    onChange={handleChangeRadio}
                                                    row // Use row prop for horizontal layout
                                                >
                                                    <FormControlLabel value="male" control={<Radio size="small" />} label="Male" />
                                                    <FormControlLabel value="female" control={<Radio size="small" />} label="Female" />

                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                variant="outlined" // Modern variant
                                                error={Boolean(touched.password && errors.password)}
                                                required
                                                fullWidth
                                                name="password"
                                                label="Password"
                                                type="password"
                                                id="password"
                                                autoComplete="new-password"
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                value={values.password}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <LockOutlinedIcon />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={handleClickShowPassword}
                                                                onMouseDown={handleMouseDownPassword}
                                                                edge="end"
                                                            >
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                            <ErrorMessage name="password">
                                                {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                                            </ErrorMessage>
                                        </Grid>
                                        <Grid item xs={12} sx={{ mt: 1 }}> {/* Added margin top */}
                                            <Typography variant="body2" gutterBottom>
                                                By clicking on enter, you accept the
                                                <Link href="/terms-condition" variant="body2" sx={{ textDecoration: 'none' }}>&nbsp;terms and conditions</Link>
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Button
                                        disabled={!isValid || !dirty || isSubmitting}
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        color="primary" // Use theme color
                                        size="large" // Larger button
                                        sx={{ mt: 3, mb: 2, py: 1.5 }} // Adjusted margins and padding
                                    >
                                        Sign Up
                                    </Button>
                                </Form>
                            )}
                        </Formik>
                    }
                </Box>
            </Paper> {/* Close Paper */}

            <Grid container justifyContent="center" sx={{ mt: 3 }}> {/* Centered link */}
                <Grid item>
                    <Link href="/login" variant="body2" sx={{ fontWeight: 'medium' }}> {/* Use sx for styling */}
                        Already have an account? Sign in
                    </Link>
                </Grid>
            </Grid>

        </Container>
    );
}


export default RegisterPage